"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const lodash_1 = require("lodash");
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const redux_1 = require("redux");
const components_1 = require("components");
const config_1 = require("config");
const merchant_portal_reducer_1 = require("merchant-portal-reducer");
const utils_1 = require("utils");
const ReportDetailsContainer_1 = __importDefault(require("./ReportDetailsContainer"));
function CreditAvailableReport({ creditAvailableReport, }) {
    const reportRef = (0, react_1.useRef)(null);
    const [summaryDetail, setSummaryDetail] = (0, react_1.useState)([]);
    const [summaryDetailLocationIds, setSummaryDetailLocationIds] = (0, react_1.useState)([]);
    const [locationData, setLocationData] = (0, react_1.useState)([]);
    const [allSelected, setAllSelected] = (0, react_1.useState)(false);
    const { summary, locationSummaries, creditAvailables } = creditAvailableReport;
    const summaryWithSummaryLabel = {
        ...summary,
        locationName: 'SUMMARY',
        locationNumber: undefined,
        checked: false,
    };
    // Effect
    (0, react_1.useEffect)(() => {
        const locationsSummary = locationSummaries.map((location) => ({
            ...location,
            checked: false,
        }));
        setLocationData([summaryWithSummaryLabel, ...locationsSummary]);
    }, []);
    const getSummaryDetailData = (0, react_1.useCallback)(() => {
        return summaryDetail.map((summary) => ({
            column01: (0, utils_1.formatLocation)(summary.locationName, summary.locationId),
            column02: summary.locationId,
            column03: summary.customerName,
            column04: summary.birthday,
            column05: summary.street,
            column06: summary.city,
            column07: summary.state,
            column08: summary.zipcode,
            column09: summary.accountNumberLast4,
            column10: (0, utils_1.formatCurrency)(summary.creditLimit),
            column11: (0, utils_1.formatCurrency)(summary.availableCredit),
            column12: summary.lastPurchaseDate,
            column13: (0, utils_1.formatISODateStringAsUSMonthDayYear)(summary.asOfDate),
        }));
    }, [summaryDetail]);
    const getLocationSummary = (0, react_1.useCallback)(() => {
        return locationData.map((location) => ({
            column01: location.locationName,
            column02: location.availableCredit,
            column03: location.totalCreditLimit,
        }));
    }, [locationData]);
    const getCSVData = () => {
        const summaryDetailData = getSummaryDetailData();
        const locationSummary = getLocationSummary();
        return [
            ...locationSummary,
            ...new Array(5).fill(config_1.EMPTY_CREDIT_AVAILABLE_REPORT_SUMMARY),
            config_1.CREDIT_AVAILABLE_DETAIL_HEADERS,
            ...summaryDetailData,
        ];
    };
    const handleDetailsChange = (0, react_1.useCallback)((locationId, event) => {
        if (event.target.checked) {
            const currentSummaryDetailLocationIds = (0, lodash_1.cloneDeep)(summaryDetailLocationIds);
            currentSummaryDetailLocationIds.push(locationId);
            setSummaryDetailLocationIds(currentSummaryDetailLocationIds);
            const updatedDetails = (0, lodash_1.cloneDeep)(summaryDetail);
            creditAvailables.forEach((creditAvailable) => {
                if (creditAvailable.locationId === locationId) {
                    updatedDetails.push(creditAvailable);
                }
            });
            setSummaryDetail(updatedDetails);
        }
        else if (!event.target.checked) {
            const currentSummaryDetailLocationIds = (0, lodash_1.cloneDeep)(summaryDetailLocationIds);
            const updatedSummaryDetailLocationIds = currentSummaryDetailLocationIds.filter((currentLocationId) => {
                return currentLocationId !== locationId;
            });
            setSummaryDetailLocationIds(updatedSummaryDetailLocationIds);
            const updatedDetails = (0, lodash_1.cloneDeep)(summaryDetail);
            const filteredDetails = updatedDetails.filter((detail) => {
                return detail.locationId !== locationId;
            });
            setSummaryDetail(filteredDetails);
        }
    }, [creditAvailables, summaryDetailLocationIds, summaryDetail]);
    const handleSelectAll = () => {
        if (!allSelected) {
            const allLocationIds = [];
            creditAvailables.forEach((creditAvailable) => {
                allLocationIds.push(creditAvailable.locationId);
            });
            setSummaryDetail(creditAvailables);
            setSummaryDetailLocationIds(allLocationIds);
        }
        else if (allSelected) {
            setSummaryDetail([]);
            setSummaryDetailLocationIds([]);
        }
        setAllSelected(!allSelected);
    };
    const columns = [
        {
            header: 'Show Details',
            enableColumnFilter: false,
            cell: (props) => {
                const rowData = props.row.original;
                return ((0, jsx_runtime_1.jsx)("span", { className: "reports-checkbox", children: rowData.locationName === 'SUMMARY' ? ((0, jsx_runtime_1.jsx)(components_1.ButtonLink, { onClick: handleSelectAll, className: "select-all button-link", children: allSelected ? 'Deselect All' : 'Select All' })) : ((0, jsx_runtime_1.jsx)("input", { type: "checkbox", checked: summaryDetailLocationIds.includes(rowData.locationId), onChange: (e) => {
                            handleDetailsChange(rowData.locationId, e);
                        } }, rowData.locationId)) }));
            },
        },
        {
            header: 'Location',
            accessorKey: 'locationName',
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            header: 'Total Accts With Credit Available',
            accessorFn: () => creditAvailables.length,
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            header: 'Total Credit Limit Outstanding',
            accessorKey: 'totalCreditLimit',
            accessorFn: (row) => (0, utils_1.formatCurrency)(row.totalCreditLimit, 2),
            enableSorting: false,
            enableColumnFilter: false,
        },
    ];
    const detailColumns = [
        {
            header: 'Location',
            accessorKey: 'locationName',
            enableColumnFilter: false,
        },
        {
            header: 'Store #',
            accessorKey: 'adsStoreNumber',
            enableColumnFilter: true,
            enableSorting: true,
        },
        {
            header: 'Customer',
            accessorKey: 'customerName',
            enableColumnFilter: true,
        },
        {
            header: 'Birthday',
            accessorKey: 'birthday',
            enableColumnFilter: false,
            enableSorting: false,
        },
        {
            header: 'Address',
            accessorKey: 'street',
            enableColumnFilter: true,
            enableSorting: false,
        },
        {
            header: 'City',
            accessorKey: 'city',
            enableColumnFilter: true,
            enableSorting: false,
        },
        {
            header: 'State',
            accessorKey: 'state',
            enableColumnFilter: true,
        },
        {
            header: 'ZIP',
            accessorKey: 'zipcode',
            enableColumnFilter: true,
            enableSorting: false,
        },
        {
            header: 'Account # Last 4',
            accessorKey: 'accountNumberLast4',
            enableColumnFilter: false,
        },
        {
            header: 'Credit Limit',
            accessorKey: 'creditLimit',
            accessorFn: (row) => (0, utils_1.formatCurrency)(row.creditLimit, 2),
            enableColumnFilter: false,
        },
        {
            header: 'Credit Available',
            accessorKey: 'availableCredit',
            accessorFn: (row) => (0, utils_1.formatCurrency)(row.availableCredit, 2),
            enableColumnFilter: false,
        },
        {
            header: 'Last Purchase Date',
            accessorKey: 'lastPurchaseDate',
            enableColumnFilter: false,
        },
        {
            header: 'Credit Updated As Of',
            accessorKey: 'asOfDate',
            enableColumnFilter: false,
        },
    ];
    return ((0, jsx_runtime_1.jsx)(ReportDetailsContainer_1.default, { csvHeaders: config_1.CREDIT_AVAILABLE_SUMMARY_HEADERS, csvFilename: "CreditAvailable.csv", csvData: getCSVData(), reportRef: reportRef, children: (0, jsx_runtime_1.jsxs)("div", { id: "report-container", ref: reportRef, children: [(0, jsx_runtime_1.jsx)("div", { className: "report-detail__headers m-t-20", children: (0, jsx_runtime_1.jsx)(components_1.H2, { className: "report-detail--title", children: "Credit Available Results" }) }), (0, jsx_runtime_1.jsxs)(components_1.P, { variant: "body-large", className: "report-detail--disclaimer", children: ["This report is generated as of ", (0, utils_1.formatISODate)(Date.now()), ", so any changes to the available credit that have occurred after that date may not be reflected here."] }), (0, jsx_runtime_1.jsx)("div", { className: "report-detail__summary m-t-50", children: (0, jsx_runtime_1.jsx)(components_1.Table, { data: locationData, columns: columns }) }), (0, jsx_runtime_1.jsx)(components_1.H3, { className: "m-t-50", children: "Details" }), (0, jsx_runtime_1.jsx)("div", { className: "report-detail__summary-detail sales-report m-t-50", children: summaryDetail.length > 0 ? ((0, jsx_runtime_1.jsx)(components_1.Table, { data: summaryDetail, columns: detailColumns })) : ((0, jsx_runtime_1.jsx)(components_1.P, { variant: "body-large", children: "Select Summary above to show Details" })) })] }) }));
}
function mapStateToProps(state) {
    return {
        creditAvailableReport: merchant_portal_reducer_1.selectors.creditAvailableReport(state),
    };
}
const mapDispatchToProps = {};
exports.default = (0, redux_1.compose)((0, react_redux_1.connect)(mapStateToProps, mapDispatchToProps))(CreditAvailableReport);
